import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/lib/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useTutorial } from '@/lib/TutorialContext';
import { GUEST_DATA_USER_ID, PUBLIC_GUEST_MODE } from '@/lib/guestMode';

const EMPTY_DATA_CONTEXT = {
  visitedCountries: [],
  bucketList: [],
  diaryEntries: [],
  dataReady: false,
  createVisited: async () => null,
  updateVisited: async () => null,
  deleteVisited: async () => null,
  createBucket: async () => null,
  updateBucket: async () => null,
  deleteBucket: async () => null,
  createDiary: async () => null,
  updateDiary: async () => null,
  deleteDiary: async () => null,
  replaceVisited: () => {},
  removeVisitedByCode: () => {},
  replaceBucket: () => {},
  refreshAll: async () => {},
};

const DataContext = createContext(EMPTY_DATA_CONTEXT);
const cachedDataKey = (userId) => `world-traveler-data:${userId}`;

function readCachedData(userId) {
  try {
    const value = JSON.parse(localStorage.getItem(cachedDataKey(userId)) || 'null');
    return value && typeof value === 'object' ? value : null;
  } catch { return null; }
}

function writeCachedData(userId, data) {
  try { localStorage.setItem(cachedDataKey(userId), JSON.stringify(data)); } catch {}
}

export function useData() {
  return useContext(DataContext) || EMPTY_DATA_CONTEXT;
}

// Retries a Supabase mutation once after refreshing the session. An expired
// or stale JWT makes auth.uid() resolve to null, so RLS rejects the write
// ("new row violates row-level security policy"). Refreshing restores a
// valid token; if the refresh token is also invalid, sign the user out and
// send them to login instead of showing a confusing RLS error.
const withAuthRetry = async (op) => {
  try {
    return await op();
  } catch (e) {
    const msg = (e?.message || '').toLowerCase();
    const isAuthError = msg.includes('row-level security') || msg.includes('jwt') || e?.code === '42501';
    if (!isAuthError) throw e;
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error || !session) {
      // Refresh failed (transient network) — don't log the user out. Throw
      // so the mutation surfaces an error, but keep them signed in to retry
      // or manually log out.
      throw new Error('Network issue — please try again');
    }
    return await op();
  }
};

const mutationQueues = new Map();

function enqueueMutation(key, operation) {
  const previous = mutationQueues.get(key) || Promise.resolve();
  const current = previous.catch(() => {}).then(operation);
  mutationQueues.set(key, current);
  current.finally(() => {
    if (mutationQueues.get(key) === current) mutationQueues.delete(key);
  }).catch(() => {});
  return current;
}

// ===== CRUD factory functions =====
// Shared create/update/delete logic parameterized by table + setter + options,
// eliminating the triplicated createVisited/createBucket/createDiary etc.
async function createRecord(config, data, ctx) {
  const { table, setter, prepend, duplicateKey, errorTitle, applyDefaults } = config;
  const { user, cancelledTempsRef, suppressedInsertsRef, toast, localOnly } = ctx;
  const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const withDefaults = applyDefaults ? applyDefaults(data) : data;
  const temp = { ...withDefaults, id: localOnly ? tempId.replace('temp-', 'guest-') : tempId, user_id: user?.id };
  setter((prev) => prepend ? [temp, ...prev] : [...prev, temp]);
  if (localOnly) return temp;
  try {
    const mutationKey = duplicateKey
      ? `${table}:${duplicateKey}:${withDefaults[duplicateKey]}`
      : `${table}:create`;
    const created = await enqueueMutation(mutationKey, () => withAuthRetry(async () => {
      const { data: row, error } = await supabase.from(table).insert({ ...withDefaults, user_id: user?.id }).select('*').single();
      if (error) {
        if (duplicateKey && error.code === '23505') {
          const { data: existing, error: fetchError } = await supabase
            .from(table).select('*').eq('user_id', user?.id).eq(duplicateKey, withDefaults[duplicateKey]).maybeSingle();
          if (fetchError) throw fetchError;
          return existing;
        }
        throw error;
      }
      return row;
    }));
    if (!created) throw new Error('The record was not returned by the server');
    if (cancelledTempsRef.current.has(tempId)) {
      cancelledTempsRef.current.delete(tempId);
      suppressedInsertsRef.current.add(created.id);
      await supabase.from(table).delete().eq('id', created.id);
      setTimeout(() => suppressedInsertsRef.current.delete(created.id), 5000);
      return created;
    }
    setter((prev) => prepend
      ? [created, ...prev.filter((r) => r.id !== tempId && r.id !== created.id)]
      : [...prev.filter((r) => r.id !== tempId && r.id !== created.id), created]);
    return created;
  } catch (e) {
    cancelledTempsRef.current.delete(tempId);
    setter((prev) => prev.filter((r) => r.id !== tempId));
    if (errorTitle) toast({ variant: 'destructive', title: errorTitle, description: e?.message || 'Unknown error' });
    throw e;
  }
}

async function updateRecord(config, id, data, localOnly = false) {
  const { table, setter } = config;
  let prevRecord = null;
  setter((prev) => prev.map((r) => {
    if (r.id === id) { prevRecord = r; return { ...r, ...data }; }
    return r;
  }));
  if (localOnly) return prevRecord ? { ...prevRecord, ...data } : null;
  try {
    const updated = await enqueueMutation(`${table}:${id}`, () => withAuthRetry(async () => {
      const { data: row, error } = await supabase.from(table).update(data).eq('id', id).select('*').single();
      if (error) throw error;
      return row;
    }));
    setter((prev) => prev.map((r) => r.id === id ? updated : r));
    return updated;
  } catch (e) {
    if (prevRecord) setter((prev) => prev.map((r) => r.id === id ? prevRecord : r));
    throw e;
  }
}

// Realtime handler factory — parameterized by setter + prepend + suppress.
function makeRealtimeHandler(setter, { prepend = false, checkSuppress = false, suppressedInsertsRef } = {}) {
  return (payload) => {
    try {
      if (payload.eventType === 'INSERT') {
        if (checkSuppress && suppressedInsertsRef.current.has(payload.new.id)) return;
        setter((prev) => prev.some((r) => r.id === payload.new.id) ? prev : (prepend ? [payload.new, ...prev] : [...prev, payload.new]));
      } else if (payload.eventType === 'UPDATE') {
        setter((prev) => prev.map((r) => r.id === payload.new.id ? payload.new : r));
      } else if (payload.eventType === 'DELETE') {
        setter((prev) => prev.filter((r) => r.id !== payload.old.id));
      }
    } catch (err) { console.error('[realtime] callback error:', err); }
  };
}

// Replace or append a record by ID (used by manual sync helpers).
function replaceRecord(setter, rec) {
  setter((prev) => {
    const idx = prev.findIndex((r) => r.id === rec.id);
    if (idx >= 0) { const next = [...prev]; next[idx] = rec; return next; }
    return [...prev, rec];
  });
}

const DATA_PAGE_SIZE = 40;
const PUBLIC_DEMO = PUBLIC_GUEST_MODE;

const yieldToBrowser = () => new Promise((resolve) => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(resolve, { timeout: 100 });
  } else {
    setTimeout(resolve, 0);
  }
});

async function fetchTableInChunks(table) {
  const rows = [];
  for (let start = 0; ; start += DATA_PAGE_SIZE) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .range(start, start + DATA_PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < DATA_PAGE_SIZE) return rows;
    await yieldToBrowser();
  }
}

// Load each entity table in small sequential pages so mobile devices do not
// download, parse, and allocate all three full datasets at the same time.
async function fetchAllData(setVisitedCountries, setBucketList, setDiaryEntries, isCurrent = () => true) {
  const load = async (table, setter) => {
    try {
      const rows = await fetchTableInChunks(table);
      if (isCurrent()) setter(rows);
    } catch {}
    await yieldToBrowser();
  };
  await load('visited_countries', setVisitedCountries);
  await load('bucket_list_countries', setBucketList);
  await load('diary_entries', setDiaryEntries);
}

async function deleteRecord(config, id, ctx) {
  const { table, setter, prepend } = config;
  const { cancelledTempsRef, localOnly } = ctx;
  if (typeof id === 'string' && id.startsWith('temp-')) {
    cancelledTempsRef.current.add(id);
    setter((prev) => prev.filter((r) => r.id !== id));
    return;
  }
  let removed = null;
  setter((prev) => {
    removed = prev.find((r) => r.id === id) || null;
    return prev.filter((r) => r.id !== id);
  });
  if (localOnly) return;
  try {
    await enqueueMutation(`${table}:${id}`, () => withAuthRetry(async () => {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
    }));
  } catch (e) {
    if (removed) setter((prev) => prepend ? [removed, ...prev] : [...prev, removed]);
    throw e;
  }
}

// Centralized data provider: holds all entity data in a single shared state,
// fetches once on login, subscribes to Supabase realtime for live updates,
// and provides optimistic mutation functions so every tab sees changes
// instantly — no per-page fetching, no loading states, no stale data.
export function DataProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const { active: tutorialActive } = useTutorial();
  const { toast } = useToast();
  const [visitedCountries, setVisitedCountries] = useState([]);
  const [bucketList, setBucketList] = useState([]);
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [dataReady, setDataReady] = useState(PUBLIC_DEMO);
  const channelRef = useRef(null);
  // Track temp records that were deleted (un-marked) before their create API
  // call returned, so the late-arriving create response can cancel itself
  // instead of re-adding the record (which caused the "blink then stuck" bug).
  const cancelledTempsRef = useRef(new Set());
  // Real IDs whose realtime INSERT should be ignored (created then immediately
  // deleted — the INSERT event would briefly re-add the record otherwise).
  const suppressedInsertsRef = useRef(new Set());
  const cacheWriteTimerRef = useRef(null);

  const tutorialVisited = useMemo(() => [
    { id: 'tutorial-fr', country_code: 'FRA', country_name: 'France', visit_count: 1, marked_regions: [] },
    { id: 'tutorial-jp', country_code: 'JPN', country_name: 'Japan', visit_count: 1, marked_regions: [] },
    { id: 'tutorial-pt', country_code: 'PRT', country_name: 'Portugal', visit_count: 1, marked_regions: [] },
    { id: 'tutorial-is', country_code: 'ISL', country_name: 'Iceland', visit_count: 1, marked_regions: [] },
  ], []);
  const tutorialBucket = useMemo(() => [
    { id: 'tutorial-nz', country_code: 'NZL', country_name: 'New Zealand', created_at: '2026-08-14T00:00:00Z', notes: 'Milford Sound and Queenstown', pictures: [], famous_places: [] },
    { id: 'tutorial-ma', country_code: 'MAR', country_name: 'Morocco', created_at: '2026-07-02T00:00:00Z', notes: 'Marrakech and the Atlas Mountains', pictures: [], famous_places: [] },
  ], []);
  const tutorialDiary = useMemo(() => [
    { id: 'tutorial-diary', country_code: 'PRT', country_name: 'Portugal', entry_date: '2026-06-18', note: '<p>A bright morning in Lisbon, with tiled streets and a long walk by the river.</p>', pictures: [], videos: [] },
  ], []);

  // Fetch all data + subscribe to realtime when authenticated
  useEffect(() => {
    if (PUBLIC_DEMO) {
      const cached = readCachedData(GUEST_DATA_USER_ID);
      setVisitedCountries(cached?.visitedCountries || []);
      setBucketList(cached?.bucketList || []);
      setDiaryEntries(cached?.diaryEntries || []);
      setDataReady(true);
      return undefined;
    }
    if (!isAuthenticated) {
      setDataReady(false);
      return;
    }

    const userId = user?.id;
    let active = true;
    const cached = userId ? readCachedData(userId) : null;
    setVisitedCountries(cached?.visitedCountries || []);
    setBucketList(cached?.bucketList || []);
    setDiaryEntries(cached?.diaryEntries || []);
    if (cached && !navigator.onLine) setDataReady(true);

    // Fetch fresh data from all three tables at once
    const fetchAll = async () => {
      await fetchAllData(setVisitedCountries, setBucketList, setDiaryEntries,
        () => active && user?.id === userId);
      if (active && user?.id === userId) setDataReady(true);
    };
    fetchAll();

    const subscribe = () => {
      if (!active || document.visibilityState !== 'visible' || channelRef.current) return;
      const channel = supabase.channel(`app-data-changes-${userId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'visited_countries', filter: `user_id=eq.${userId}` },
          makeRealtimeHandler(setVisitedCountries, { checkSuppress: true, suppressedInsertsRef }))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bucket_list_countries', filter: `user_id=eq.${userId}` },
          makeRealtimeHandler(setBucketList))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'diary_entries', filter: `user_id=eq.${userId}` },
          makeRealtimeHandler(setDiaryEntries, { prepend: true }))
        .subscribe((status) => {
          if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && active) {
            fetchAllData(setVisitedCountries, setBucketList, setDiaryEntries,
              () => active && user?.id === userId).catch(() => {});
          }
        });
      channelRef.current = channel;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchAllData(setVisitedCountries, setBucketList, setDiaryEntries,
          () => active && user?.id === userId).catch(() => {});
        subscribe();
      } else if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
    subscribe();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisibility);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    const cacheUserId = PUBLIC_DEMO ? GUEST_DATA_USER_ID : user?.id;
    if (!cacheUserId) return undefined;
    clearTimeout(cacheWriteTimerRef.current);
    cacheWriteTimerRef.current = setTimeout(() => {
      writeCachedData(cacheUserId, { visitedCountries, bucketList, diaryEntries });
      cacheWriteTimerRef.current = null;
    }, 800);
    return () => clearTimeout(cacheWriteTimerRef.current);
  }, [user?.id, visitedCountries, bucketList, diaryEntries]);

  // Clear data on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setVisitedCountries([]);
      setBucketList([]);
      setDiaryEntries([]);
    }
  }, [isAuthenticated]);

  // ===== Optimistic mutation functions =====
  // Each function updates local state immediately, then calls the API.
  // If the API fails, the optimistic change is rolled back.
  // Realtime subscriptions also fire on success, but the optimistic update
  // makes the UI feel instant without waiting for the round-trip.

  const createVisited = useCallback((data) => createRecord(
    { table: 'visited_countries', setter: setVisitedCountries, prepend: false,
      duplicateKey: 'country_code', errorTitle: 'Could not mark country',
      applyDefaults: (d) => d.visit_count == null && !d.marked_regions?.length ? { ...d, visit_count: 1 } : d },
    data, { user, cancelledTempsRef, suppressedInsertsRef, toast, localOnly: PUBLIC_DEMO }
  ), [user, toast]);

  const updateVisited = useCallback((id, data) => updateRecord(
    { table: 'visited_countries', setter: setVisitedCountries }, id, data, PUBLIC_DEMO
  ), []);

  const deleteVisited = useCallback((id) => deleteRecord(
    { table: 'visited_countries', setter: setVisitedCountries, prepend: false },
    id, { cancelledTempsRef, localOnly: PUBLIC_DEMO }
  ), []);

  const createBucket = useCallback((data) => createRecord(
    { table: 'bucket_list_countries', setter: setBucketList, prepend: true,
      duplicateKey: 'country_code' },
    data, { user, cancelledTempsRef, suppressedInsertsRef, toast, localOnly: PUBLIC_DEMO }
  ), [user, toast]);

  const updateBucket = useCallback((id, data) => updateRecord(
    { table: 'bucket_list_countries', setter: setBucketList }, id, data, PUBLIC_DEMO
  ), []);

  const deleteBucket = useCallback((id) => deleteRecord(
    { table: 'bucket_list_countries', setter: setBucketList, prepend: true },
    id, { cancelledTempsRef, localOnly: PUBLIC_DEMO }
  ), []);

  const createDiary = useCallback((data) => createRecord(
    { table: 'diary_entries', setter: setDiaryEntries, prepend: true },
    data, { user, cancelledTempsRef, suppressedInsertsRef, toast, localOnly: PUBLIC_DEMO }
  ), [user, toast]);

  const updateDiary = useCallback((id, data) => updateRecord(
    { table: 'diary_entries', setter: setDiaryEntries }, id, data, PUBLIC_DEMO
  ), []);

  const deleteDiary = useCallback((id) => deleteRecord(
    { table: 'diary_entries', setter: setDiaryEntries, prepend: true },
    id, { cancelledTempsRef, localOnly: PUBLIC_DEMO }
  ), []);

  // Manual sync helpers — used by callbacks from components that still
  // call the model functions directly (e.g. CountryPanel). These update
  // the shared state immediately so all pages stay in sync even without
  // realtime.
  const replaceVisited = useCallback((rec) => replaceRecord(setVisitedCountries, rec), []);
  const removeVisitedByCode = useCallback((code) => setVisitedCountries((prev) => prev.filter((r) => r.country_code !== code)), []);
  const replaceBucket = useCallback((rec) => replaceRecord(setBucketList, rec), []);

  // Re-fetch all data from the API (fallback for components that modify
  // data outside the context's mutation functions).
  const refreshAll = useCallback(async () => {
    const userId = user?.id;
    await fetchAllData(setVisitedCountries, setBucketList, setDiaryEntries,
      () => user?.id === userId);
  }, [user?.id]);

  const value = useMemo(() => ({
    visitedCountries: tutorialActive ? tutorialVisited : visitedCountries,
    bucketList: tutorialActive ? tutorialBucket : bucketList,
    diaryEntries: tutorialActive ? tutorialDiary : diaryEntries,
    dataReady,
    createVisited: tutorialActive ? async () => null : createVisited,
    updateVisited: tutorialActive ? async () => null : updateVisited,
    deleteVisited: tutorialActive ? async () => null : deleteVisited,
    createBucket: tutorialActive ? async () => null : createBucket,
    updateBucket: tutorialActive ? async () => null : updateBucket,
    deleteBucket: tutorialActive ? async () => null : deleteBucket,
    createDiary: tutorialActive ? async () => null : createDiary,
    updateDiary: tutorialActive ? async () => null : updateDiary,
    deleteDiary: tutorialActive ? async () => null : deleteDiary,
    replaceVisited,
    removeVisitedByCode,
    replaceBucket,
    refreshAll,
  }), [tutorialActive, tutorialVisited, tutorialBucket, tutorialDiary, visitedCountries, bucketList, diaryEntries, dataReady,
    createVisited, updateVisited, deleteVisited, createBucket, updateBucket, deleteBucket,
    createDiary, updateDiary, deleteDiary, replaceVisited, removeVisitedByCode, replaceBucket, refreshAll]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}